import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, User, UserCheck, UserX, AlertCircle, Search, Loader2, Upload, Users, Info } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { userService } from '@/services/chat/userService';
import { teamService } from '@/services/teamService';
import { gameRosterService } from '@/services/gameRosterService';
import { getUserTypeDisplayName, getUserTypeBadgeVariant } from '@/utils/userTypeDisplay';
import { User as ChatUser } from '@/types/chat';
import { isAgeCompatible, normalizeAgeGroup } from '@/utils/ageUtils';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';

interface Player {
  id: string;
  name: string;
  username?: string;
  position?: string;
  gameStatus: 'ready' | 'present' | 'injured' | 'no_show' | 'disqualified' | 'unavailable' | 'bench' | 'starter' | 'substitute' | 'ejected';
  rosterId?: string;
  isTeamPlayer?: boolean;
  userType?: 'internal' | 'supabase';
  disqualificationReason?: string;
}

interface TeamRosterManagementProps {
  gameId: string;
  teamId: number;
  teamSide: 'home' | 'away';
  teamName: string;
  canEdit: boolean;
  onPlayersChange?: (players: Player[]) => void;
}

export const TeamRosterManagement: React.FC<TeamRosterManagementProps> = ({
  gameId,
  teamId,
  teamSide,
  teamName,
  canEdit,
  onPlayersChange
}) => {
  const [rosterPlayers, setRosterPlayers] = useState<Player[]>([]);
  const [availablePlayers, setAvailablePlayers] = useState<Player[]>([]);
  const [teamPlayers, setTeamPlayers] = useState<Player[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [gameAgeGroup, setGameAgeGroup] = useState<string | null>(null);
  const { toast } = useToast();
  const { user: unifiedUser } = useUnifiedUser();

  // Load existing roster
  useEffect(() => {
    if (gameId && teamId) {
      loadGameRoster();
      loadTeamPlayersAndAvailable();
    }
  }, [gameId, teamId]);

  // Reload available players when roster changes
  useEffect(() => {
    if (teamId) {
      loadTeamPlayersAndAvailable();
    }
  }, [rosterPlayers]);

  const loadGameRoster = async () => {
    try {
      setLoading(true);
      
      // Get roster players with user data
      const { data: rosterData, error: rosterError } = await supabase
        .from('game_rosters_live')
        .select('id, player_id, position, is_present, is_eligible, player_type, game_day_status')
        .eq('game_id', gameId)
        .eq('team_id', teamId);

      if (rosterError) throw rosterError;

      if (rosterData && rosterData.length > 0) {
        const players: Player[] = [];
        
        // Separate Supabase and internal players
        const supabasePlayerIds = rosterData.filter(r => r.player_type === 'supabase').map(r => r.player_id);
        const internalPlayerIds = rosterData.filter(r => r.player_type === 'internal').map(r => r.player_id);

        // Get Supabase profiles
        if (supabasePlayerIds.length > 0) {
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('id, username, first_name, last_name')
            .in('id', supabasePlayerIds);

          if (profilesData) {
            rosterData.filter(r => r.player_type === 'supabase').forEach(player => {
              const profile = profilesData.find(p => p.id === player.player_id);
              players.push({
                id: player.player_id,
                name: profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 'Unknown Player',
                username: profile?.username,
                position: player.position || 'Player',
                gameStatus: (player.game_day_status || 'ready') as Player['gameStatus'],
                rosterId: player.id,
                userType: 'supabase'
              });
            });
          }
        }

        // Get internal profiles
        if (internalPlayerIds.length > 0) {
          const { data: internalProfiles } = await supabase
            .from('internal_users')
            .select('id, username')
            .in('id', internalPlayerIds);

          if (internalProfiles) {
            rosterData.filter(r => r.player_type === 'internal').forEach(player => {
              const profile = internalProfiles.find(p => p.id === player.player_id);
              players.push({
                id: player.player_id,
                name: profile?.username || 'Unknown Player',
                username: profile?.username,
                position: player.position || 'Player',
                gameStatus: (player.game_day_status || 'ready') as Player['gameStatus'],
                rosterId: player.id,
                userType: 'internal'
              });
            });
          }
        }

        setRosterPlayers(players);
        onPlayersChange?.(players);
      }
    } catch (error) {
      console.error('Error loading roster:', error);
      toast({
        title: "Error",
        description: "Failed to load team roster",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTeamPlayersAndAvailable = async () => {
    try {
      // Get current team players using the team service
      const teamPlayersData = await teamService.getTeamPlayers(teamId);
      
      // Convert team players to our Player format
      const currentTeamPlayers = teamPlayersData.map(tp => {
        // tp might have user_profile and user_type from the service, or be basic TeamPlayer
        const profile = (tp as any).user_profile;
        const userType = (tp as any).user_type || 'supabase';
        const isSupabase = userType === 'supabase';
        
        return {
          id: tp.user_id,
          name: profile 
            ? (isSupabase 
                ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown Player'
                : profile.username || 'Unknown Player')
            : 'Unknown Player',
          username: profile?.username || `user_${tp.user_id.slice(0, 8)}`,
          gameStatus: 'ready' as const,
          isTeamPlayer: true,
          userType: userType as 'internal' | 'supabase'
        };
      });

      setTeamPlayers(currentTeamPlayers);

      // Get game age group for filtering
      let gameAgeGroup: string | null = null;
      
      // Try venue_games first
      const { data: venueGame } = await supabase
        .from('venue_games')
        .select('age_group')
        .eq('id', gameId)
        .single();
      
      if (venueGame?.age_group) {
        gameAgeGroup = venueGame.age_group;
      } else {
        // Try games table
        const { data: game } = await supabase
          .from('games')
          .select('age_group')
          .eq('id', gameId)
          .single();
        
        if (game?.age_group) {
          gameAgeGroup = game.age_group;
        }
      }

      // If no game age group, fall back to team age group
      if (!gameAgeGroup) {
        const { data: team } = await supabase
          .from('teams')
          .select('age_group')
          .eq('team_id', teamId)
          .single();
        
        gameAgeGroup = team?.age_group || '18-plus'; // Default fallback
      }

      console.log(`🎯 Game age group: ${gameAgeGroup}, filtering age-compatible players...`);
      
      // Store game age group for UI display
      setGameAgeGroup(gameAgeGroup);

      // Get age-compatible available users using the unified user service
      const allUsers = await userService.getAgeCompatibleUsers(gameAgeGroup as any, 'player');
      
      // Convert all users to Player format
      const allAvailablePlayers = allUsers.map(user => ({
        id: user.id,
        name: user.user_type === 'supabase' 
          ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username
          : user.username,
        username: user.username,
        gameStatus: 'ready' as const,
        isTeamPlayer: currentTeamPlayers.some(tp => tp.id === user.id),
        userType: user.user_type
      }));

      // Filter out players already in roster
      const availableOnly = allAvailablePlayers.filter(
        player => !rosterPlayers.some(rp => rp.id === player.id)
      );

      setAvailablePlayers(availableOnly);
    } catch (error) {
      console.error('Error loading players:', error);
      toast({
        title: "Error",
        description: "Failed to load available players",
        variant: "destructive"
      });
    }
  };

  const addPlayerToRoster = async (playerId: string, position?: string) => {
    try {
      setLoading(true);
      console.log('🎯 Adding player to roster:', { gameId, teamId, playerId, position });
      console.log('🔍 Unified user:', unifiedUser);
      
      // Check if user is authenticated
      if (!unifiedUser) {
        console.error('❌ No authenticated user found');
        toast({
          title: "Authentication Error",
          description: "You must be logged in to add players",
          variant: "destructive"
        });
        return;
      }

      const player = availablePlayers.find(p => p.id === playerId);
      const playerType = player?.userType || 'supabase';
      
      console.log('🔍 Found player data:', player);
      console.log('👤 Current user ID:', unifiedUser.id);

      // Use the service with the authenticated user's ID
      await gameRosterService.addPlayerToGameRoster(
        gameId,
        teamId,
        playerId,
        playerType,
        teamSide,
        position,
        unifiedUser.id
      );

      console.log('✅ Player added successfully');
      toast({
        title: "Success",
        description: `Player added to ${teamSide} team roster`,
        variant: "default"
      });

      // Reload the roster to reflect changes
      await loadGameRoster();
    } catch (error: any) {
      console.error('❌ Error adding player to roster:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add player to roster",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const bulkImportTeamPlayers = async () => {
    try {
      setBulkImporting(true);
      
      console.log('🔍 Debug - Team players:', teamPlayers.length);
      console.log('🔍 Debug - Roster players:', rosterPlayers.length);
      console.log('🔍 Debug - Team players data:', teamPlayers);
      console.log('🔍 Debug - Roster players data:', rosterPlayers);
      console.log('🔍 Unified user for import:', unifiedUser);
      
      // Check if user is authenticated
      if (!unifiedUser) {
        console.error('❌ No authenticated user found for import');
        toast({
          title: "Authentication Error",
          description: "You must be logged in to import players",
          variant: "destructive"
        });
        return;
      }

      console.log('🔒 Auth successful for bulk import, user:', unifiedUser.id);
      
      // Filter team players not already in roster
      // Compare team player id with roster player id (both represent the user_id)
      const playersToAdd = teamPlayers.filter(
        tp => !rosterPlayers.some(rp => rp.id === tp.id)
      );

      console.log('🔍 Debug - Players to add:', playersToAdd.length);
      console.log('🔍 Debug - Players to add data:', playersToAdd);

      if (playersToAdd.length === 0) {
        toast({
          title: "No New Players",
          description: "All team members are already in the roster"
        });
        return;
      }

      console.log(`🎯 Importing ${playersToAdd.length} team players to game roster...`);

      // Add all team players in parallel to both tables
      const insertPromises = playersToAdd.map(async (player) => {
        console.log(`🔄 Preparing to add player: ${player.name} (${player.id})`);
        
        // Insert into game_rosters_live
        const rosterData = {
          game_id: gameId,
          team_id: teamId,
          player_id: player.id,
          player_type: player.userType || 'supabase',
          team_side: teamSide,
          position: 'Player',
          is_present: true,
          is_eligible: true,
          game_day_status: 'ready',
          added_by: unifiedUser.id
        };
        
        // Insert into game_participants  
        const participantData = {
          game_id: gameId,
          player_id: player.id,
          player_type: player.userType || 'supabase',
          team_side: teamSide,
          participation_status: 'confirmed',
          eligibility_status: 'eligible',
          requested_by: unifiedUser.id
        };
        
        console.log(`📝 Insert data for ${player.name}:`, { rosterData, participantData });
        
        // Insert both records
        const [rosterResult, participantResult] = await Promise.all([
          supabase.from('game_rosters_live').insert(rosterData).select(),
          supabase.from('game_participants').insert(participantData).select()
        ]);
        
        return { roster: rosterResult, participant: participantResult };
      });

      console.log(`🚀 Executing ${insertPromises.length} insert operations...`);
      const results = await Promise.allSettled(insertPromises);
      
      // Check for any failures
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      
      console.log(`✅ Import results: ${successful} successful, ${failed} failed`);
      
      if (failed > 0) {
        console.error(`❌ Failed to import ${failed} players:`, results.filter(r => r.status === 'rejected'));
        
        // Log detailed error information
        results.filter(r => r.status === 'rejected').forEach((result, index) => {
          const reason = (result as PromiseRejectedResult).reason;
          console.error(`❌ Import error ${index + 1}:`, reason);
          if (reason?.error) {
            console.error(`   Database error details:`, reason.error);
          }
        });
      }

      console.log('🔄 Reloading game roster...');
      await loadGameRoster();

      if (successful > 0) {
        toast({
          title: "Players Imported",
          description: `Successfully added ${successful} team members to the roster${failed > 0 ? `. ${failed} failed to import.` : ''}`
        });
      } else {
        const firstError = results.find(r => r.status === 'rejected') as PromiseRejectedResult;
        const errorMessage = firstError?.reason?.error?.message || 'Unknown error';
        throw new Error(`All player imports failed: ${errorMessage}`);
      }
    } catch (error: any) {
      console.error('💥 Error bulk importing players:', error);
      toast({
        title: "Import Failed",
        description: error.message || "Failed to import team players",
        variant: "destructive"
      });
    } finally {
      setBulkImporting(false);
    }
  };

  const updatePlayerStatus = async (playerId: string, status: Player['gameStatus']) => {
    try {
      const player = rosterPlayers.find(p => p.id === playerId);
      if (!player?.rosterId) return;

      // Map status to database fields for game_rosters_live table
      const updateData: any = {
        game_day_status: status
      };
      
      // Set is_present based on status
      updateData.is_present = ['present', 'ready', 'starter', 'bench', 'substitute'].includes(status);
      
      // Set is_eligible based on status
      updateData.is_eligible = !['injured', 'disqualified', 'ejected'].includes(status);

      const { error } = await supabase
        .from('game_rosters_live')
        .update(updateData)
        .eq('id', player.rosterId);

      if (error) throw error;

      setRosterPlayers(prev => 
        prev.map(p => p.id === playerId ? { ...p, gameStatus: status } : p)
      );

      toast({
        title: "Status Updated",
        description: `Player status updated to ${status.replace('_', ' ')}`
      });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update player status",
        variant: "destructive"
      });
    }
  };

  const removePlayerFromRoster = async (playerId: string) => {
    try {
      const player = rosterPlayers.find(p => p.id === playerId);
      if (!player?.rosterId) return;

      const { error } = await supabase
        .from('game_rosters_live')
        .delete()
        .eq('id', player.rosterId);

      if (error) throw error;

      await loadGameRoster();

      toast({
        title: "Player Removed",
        description: "Player removed from game roster"
      });
    } catch (error) {
      console.error('Error removing player:', error);
      toast({
        title: "Error",
        description: "Failed to remove player",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status: Player['gameStatus']) => {
    const variants = {
      ready: { variant: 'default' as const, icon: UserCheck, color: 'bg-emerald-500 text-white', label: 'Ready/Confirmed' },
      injured: { variant: 'destructive' as const, icon: AlertCircle, color: 'bg-red-500 text-white', label: 'Injured' },
      unavailable: { variant: 'secondary' as const, icon: UserX, color: 'bg-gray-500 text-white', label: 'Unavailable' },
      // Legacy/game-action statuses kept for display compatibility
      present: { variant: 'default' as const, icon: UserCheck, color: 'bg-emerald-600 text-white', label: 'Ready/Confirmed' },
      starter: { variant: 'default' as const, icon: UserCheck, color: 'bg-emerald-500 text-white', label: 'Ready/Confirmed' },
      bench: { variant: 'default' as const, icon: UserCheck, color: 'bg-emerald-500 text-white', label: 'Ready/Confirmed' },
      no_show: { variant: 'secondary' as const, icon: UserX, color: 'bg-gray-500 text-white', label: 'Unavailable' },
      substitute: { variant: 'default' as const, icon: UserCheck, color: 'bg-emerald-500 text-white', label: 'Ready/Confirmed' },
      disqualified: { variant: 'destructive' as const, icon: AlertCircle, color: 'bg-red-700 text-white', label: 'Disqualified' },
      ejected: { variant: 'destructive' as const, icon: AlertCircle, color: 'bg-red-800 text-white', label: 'Ejected' }
    };

    const config = variants[status] || variants.unavailable;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className={`flex items-center gap-1 ${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const filteredAvailablePlayers = availablePlayers.filter(player => {
    const searchLower = searchTerm.toLowerCase();
    const cleanSearch = searchLower.startsWith('@') ? searchLower.slice(1) : searchLower;
    
    return (
      player.name.toLowerCase().includes(cleanSearch) ||
      player.username?.toLowerCase().includes(cleanSearch)
    );
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {teamName} Roster
          </div>
          {canEdit && (
            <div className="flex gap-2">
              {teamPlayers.length > 0 && (
                <Button
                  onClick={bulkImportTeamPlayers}
                  size="sm"
                  variant="secondary"
                  disabled={bulkImporting}
                >
                  {bulkImporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Import Team Members
                </Button>
              )}
              <Button
                onClick={() => setShowAddPlayer(!showAddPlayer)}
                size="sm"
                variant="outline"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Individual Player
              </Button>
            </div>
          )}
        </CardTitle>
        <CardDescription>
          <div className="space-y-2">
            <div>
              Manage your team roster and player availability for this game.{' '}
              {teamPlayers.length > 0 && (
                <span className="text-sm">
                  Team has {teamPlayers.length} member{teamPlayers.length === 1 ? '' : 's'} available.
                </span>
              )}
            </div>
            {gameAgeGroup && (
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  Game Age Group: {gameAgeGroup}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  Only age-compatible players are shown
                </span>
              </div>
            )}
          </div>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add Player Section */}
        {showAddPlayer && canEdit && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Add Players</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or @username (both Old School & New School players)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="grid gap-2 max-h-40 overflow-y-auto">
                  {filteredAvailablePlayers.map(player => (
                    <div key={player.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                         <div className="flex items-center gap-2">
                           <p className="font-medium">{player.name}</p>
                           {player.isTeamPlayer && (
                             <Badge variant="secondary" className="text-xs">Team Member</Badge>
                           )}
                           <Badge 
                             variant={getUserTypeBadgeVariant(player.userType || 'supabase')} 
                             className="text-xs"
                           >
                             {getUserTypeDisplayName(player.userType || 'supabase')}
                           </Badge>
                         </div>
                        <p className="text-sm text-muted-foreground">
                          @{player.username}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          console.log('🎯 Add Player button clicked for:', player.id, player.name);
                          addPlayerToRoster(player.id);
                        }}
                        variant={player.isTeamPlayer ? "default" : "outline"}
                        className={player.isTeamPlayer ? "bg-green-600 hover:bg-green-700 text-white" : ""}
                        disabled={loading}
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
                      </Button>
                    </div>
                  ))}
                  {filteredAvailablePlayers.length === 0 && (
                    <div className="text-center text-muted-foreground py-4">
                      <Users className="h-8 w-8 mx-auto mb-2" />
                      <p>No available players found</p>
                      <p className="text-xs">Try searching with @ symbol or different keywords</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Player Availability */}
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold">Player Availability ({rosterPlayers.length} players)</h4>
            <p className="text-sm text-muted-foreground">Set each player's availability. Starting lineup is managed in the section below.</p>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : rosterPlayers.length > 0 ? (
            <div className="grid gap-3">
              {rosterPlayers.map(player => (
                <Card key={player.id}>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
                      <div className="flex items-center gap-3 flex-1 w-full min-w-0">
                        <div className="flex-1 min-w-0">
                           <div className="flex items-center gap-2 flex-wrap">
                             <p className="text-base md:text-lg font-medium">{player.name}</p>
                             <Badge 
                               variant={getUserTypeBadgeVariant(player.userType || 'supabase')} 
                               className="text-xs"
                             >
                               {getUserTypeDisplayName(player.userType || 'supabase')}
                             </Badge>
                           </div>
                          <p className="text-sm md:text-base text-muted-foreground">
                            @{player.username} • {player.position}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 w-full md:w-auto">
                        {getStatusBadge(player.gameStatus)}
                        {canEdit && (
                          <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                            <Select
                              value={player.gameStatus}
                              onValueChange={(value) => updatePlayerStatus(player.id, value as Player['gameStatus'])}
                            >
                               <SelectTrigger className="w-full md:w-40 h-11">
                                 <SelectValue />
                               </SelectTrigger>
                                <SelectContent className="z-50 bg-background">
                                  <SelectItem value="ready" title="Player is confirmed and available to play">
                                    Ready/Confirmed
                                  </SelectItem>
                                  <SelectItem value="injured" title="Player is injured and cannot play">
                                    Injured
                                  </SelectItem>
                                  <SelectItem value="unavailable" title="Player cannot attend the game">
                                    Unavailable
                                  </SelectItem>
                                </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => removePlayerFromRoster(player.id)}
                              className="w-full md:w-auto"
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No players in roster yet</p>
                {canEdit && (
                  <Button onClick={() => setShowAddPlayer(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Player
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>

         {/* Roster Summary */}
         <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
           <div className="text-center">
             <p className="text-2xl font-bold">{rosterPlayers.length}</p>
             <p className="text-sm text-muted-foreground">Total Players</p>
           </div>
           <div className="text-center">
             <p className="text-2xl font-bold text-emerald-600">
               {rosterPlayers.filter(p => p.gameStatus === 'ready').length}
             </p>
             <p className="text-sm text-muted-foreground">Ready/Confirmed</p>
           </div>
           <div className="text-center">
             <p className="text-2xl font-bold text-red-600">
               {rosterPlayers.filter(p => ['injured', 'unavailable'].includes(p.gameStatus)).length}
             </p>
             <p className="text-sm text-muted-foreground">Injured / Unavailable</p>
           </div>
         </div>
      </CardContent>
    </Card>
  );
};